import * as React from "react";

import { cn } from "../lib/utils";

/**
 * v2 Skeleton (redesign phase 5, #455). Storybook only until implementation;
 * see docs/design-library/04-implementation-notes.md for the swap.
 *
 * One colour, `--muted`, in both apps (break #71; Neptune's `primary/10`
 * goes). Pulses with Tailwind's `animate-pulse`, which chat.css steps at 10
 * changes per second; static under reduced motion.
 *
 * Shapes:
 * - `block` (default): radius 6, size from `className`, like v1.
 * - `line`: one line of 14/20 text, a 12px bar with 4px above and below so
 *   the loaded row has the same height; radius 4. Width from `className`.
 * - `circle`: avatars and icons, size from `className` (`size-8`).
 *
 * Always `aria-hidden`: the loading region says it is loading, not each bar.
 * Give that region `aria-busy="true"` and a status (a Throbber, or a visually
 * hidden `role="status"` text).
 */

export type SkeletonShape = "block" | "line" | "circle";

const SHAPE: Record<SkeletonShape, string> = {
  block: "rounded-md",
  line: "my-1 h-3 w-full rounded-sm",
  circle: "rounded-full",
};

export interface SkeletonProps extends React.HTMLAttributes<HTMLDivElement> {
  shape?: SkeletonShape;
}

const Skeleton = React.forwardRef<HTMLDivElement, SkeletonProps>(function Skeleton(
  { shape = "block", className, ...props },
  ref,
) {
  return (
    <div
      ref={ref}
      aria-hidden="true"
      data-shape={shape}
      className={cn("shrink-0 animate-pulse bg-muted motion-reduce:animate-none", SHAPE[shape], className)}
      {...props}
    />
  );
});

export { Skeleton };
